import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleEndReason,
	EditorRemixLifecycleEvent,
	EditorRemixLifecycleHandle,
	EditorRemixLifecycleStartRequest,
} from './types';

export type EditorRemixLifecycleToken = symbol;

export type RetainedEditorRemixLifecycle = {
	conversationId: string;
	remixId: string;
	token: EditorRemixLifecycleToken;
	/** Returns true only when this call released ownership; a stale or already-settled handle is a no-op. */
	rollback: () => boolean;
	transition: (event: EditorRemixLifecycleEvent) => boolean;
	hasAcceptedPreview: () => boolean;
	/** Returns true only when this call released ownership; a stale or already-settled handle is a no-op. */
	end: (reason: EditorRemixLifecycleEndReason) => boolean;
	setOwnerDestructionEndReason: (reason: EditorRemixLifecycleEndReason | undefined) => void;
};

export type EditorRemixLifecycleOwner = {
	active: RetainedEditorRemixLifecycle | undefined;
	retain: (handle: EditorRemixLifecycleHandle) => RetainedEditorRemixLifecycle | undefined;
};

export type EditorRemixLifecycleRetentionOutcome =
	| { status: 'legacy' }
	| { status: 'retained'; lifecycle: RetainedEditorRemixLifecycle }
	| { status: 'active-flow-rejected' }
	| { status: 'infrastructure-failure' };

/**
 * Keeps admission and owner retention semantics identical for create and update producers.
 * Infrastructure failures fail open to legacy; a real owner collision fails closed.
 */
export const admitAndRetainEditorRemixLifecycle = ({
	adapter,
	owner,
	request,
}: {
	adapter: EditorRemixLifecycleAdapter | undefined;
	owner: EditorRemixLifecycleOwner | undefined;
	request: EditorRemixLifecycleStartRequest;
}): EditorRemixLifecycleRetentionOutcome => {
	if (!adapter) {
		return { status: 'legacy' };
	}

	let admission;
	try {
		admission = adapter.startFlow(request);
	} catch {
		return { status: 'infrastructure-failure' };
	}

	if (admission.status === 'active-flow-rejected') {
		return { status: 'active-flow-rejected' };
	}
	if (admission.status !== 'admitted') {
		return admission.status === 'shadow-infrastructure-failure'
			? { status: 'infrastructure-failure' }
			: { status: 'legacy' };
	}

	if (!owner) {
		admission.handle.rollback();
		return { status: 'infrastructure-failure' };
	}

	const retained = owner.retain(admission.handle);
	if (!retained) {
		admission.handle.rollback();
		return { status: 'active-flow-rejected' };
	}

	return { status: 'retained', lifecycle: retained };
};

type RetainedEntry = {
	actions: RetainedEditorRemixLifecycle;
	handle: EditorRemixLifecycleHandle;
	ownerDestructionEndReason?: EditorRemixLifecycleEndReason;
	token: EditorRemixLifecycleToken;
};

const EditorRemixLifecycleOwnerContext = createContext<EditorRemixLifecycleOwner | undefined>(
	undefined,
);

export const useEditorRemixLifecycleOwner = (): EditorRemixLifecycleOwner | undefined =>
	useContext(EditorRemixLifecycleOwnerContext);

export const EditorRemixLifecycleOwnerProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const activeRef = useRef<RetainedEntry | undefined>(undefined);
	const [active, setActive] = useState<RetainedEditorRemixLifecycle | undefined>(undefined);

	const retain = useCallback(
		(handle: EditorRemixLifecycleHandle): RetainedEditorRemixLifecycle | undefined => {
			if (activeRef.current) {
				return undefined;
			}

			const token = Symbol('editor-remix-lifecycle');
			let hasAcceptedPreview = false;
			const isCurrent = () => activeRef.current?.token === token;
			const release = () => {
				if (!isCurrent()) {
					return false;
				}
				activeRef.current = undefined;
				setActive(undefined);
				return true;
			};

			const actions: RetainedEditorRemixLifecycle = {
				conversationId: handle.conversationId,
				remixId: handle.remixId,
				token,
				rollback: () => {
					if (!release()) {
						return false;
					}
					handle.rollback();
					return true;
				},
				transition: (event) => {
					if (!isCurrent()) {
						return false;
					}
					const accepted = handle.transition?.(event) ?? false;
					if (accepted && event.type === 'PREVIEW_OPENED') {
						hasAcceptedPreview = true;
					}
					if (accepted && event.type === 'INSERTED') {
						release();
					}
					return accepted;
				},
				hasAcceptedPreview: () => isCurrent() && hasAcceptedPreview,
				end: (reason) => {
					if (!release()) {
						return false;
					}
					handle.end?.(reason);
					return true;
				},
				setOwnerDestructionEndReason: (reason) => {
					if (isCurrent() && activeRef.current) {
						activeRef.current.ownerDestructionEndReason = reason;
					}
				},
			};

			activeRef.current = { actions, handle, token };
			setActive(actions);
			return actions;
		},
		[],
	);

	useEffect(
		() => () => {
			const retained = activeRef.current;
			activeRef.current = undefined;
			if (retained?.ownerDestructionEndReason) {
				retained.handle.end?.(retained.ownerDestructionEndReason);
			} else {
				retained?.handle.rollback();
			}
		},
		[],
	);

	const value = useMemo(() => ({ active, retain }), [active, retain]);

	return (
		<EditorRemixLifecycleOwnerContext.Provider value={value}>
			{children}
		</EditorRemixLifecycleOwnerContext.Provider>
	);
};
